import { type QueryKey } from '@tanstack/react-query';
import { memo, type ReactElement, useMemo } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  useProjectFilter,
  useProjectMetricThresholds,
  useProjectsQuery,
} from '@/features/projects';
import { suitesGridConfig } from '@/features/suites/grid/gridConfig';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { type GridFilterModel, type GridSortModel } from '@/ui/primitives';
import { buildSuitesColumns } from './suitesColumns';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  MetricThresholdGroup,
  QueryResult,
  SuiteExecutionStatus,
  SuiteListItemDto,
} from '@flowqa/shared';

type SuitesTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<SuiteListItemDto>>;
  initialData?: QueryResult<SuiteListItemDto>;
  queryKey?: QueryKey;
  onRowsChange?: (rows: SuiteListItemDto[]) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  onStatusSelect?: (status: SuiteExecutionStatus | null | undefined) => void;
  showDeltas?: boolean;
  tokenOverride?: string | null;
  disableColumnSorting?: boolean;
  disableLinks?: boolean;
};

const SuitesTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  noRowsText = 'No suites found',
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  initialData,
  queryKey,
  onRowsChange,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  onStatusSelect,
  showDeltas = true,
  tokenOverride,
  disableColumnSorting,
  disableLinks = false,
}: SuitesTableProps): ReactElement => {
  const { session } = useAuth();
  const resolvedToken = tokenOverride ?? session?.tokens.accessToken ?? null;
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const projectsQuery = useProjectsQuery();
  const thresholdsByProjectId = useMemo(() => {
    const entries = projectsQuery.data?.items ?? [];
    return new Map(entries.map((project) => [project.id, project.metricThresholds.suites]));
  }, [projectsQuery.data?.items]);
  const resolveThresholds = useMemo(
    () =>
      (row: { projectId: string }): MetricThresholdGroup | undefined =>
        thresholdsByProjectId.get(row.projectId),
    [thresholdsByProjectId],
  );
  const defaultColumns = useMemo(
    () =>
      buildSuitesColumns({
        selectedProjectId,
        onProjectSelect: setSelectedProjectId,
        onStatusSelect,
        thresholds: thresholds.suites,
        resolveThresholds,
        showDeltas,
        disableLinks,
      }),
    [
      disableLinks,
      onStatusSelect,
      resolveThresholds,
      selectedProjectId,
      setSelectedProjectId,
      showDeltas,
      thresholds.suites,
    ],
  );

  const gridConfig = suitesGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey ?? 'suites-table',
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    includeDeltas: showDeltas,
  });
  const gridProps = buildTableGridProps<SuiteListItemDto>({
    gridConfig,
    queryFn,
    queryKey,
    pageSizeOptions,
    initialPageSize,
    disablePageSizeSelect,
    hideFooter,
    noRowsText,
    onRowsChange,
    onQueryInput,
    onDataUpdatedAt,
    showLastUpdated,
  });

  return (
    <ServerDataTable<SuiteListItemDto>
      queryPath="suites/query"
      {...gridConfig}
      {...gridProps}
      initialData={initialData}
      columns={defaultColumns}
      getRowId={(row) => row.id}
      token={resolvedToken}
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      disableColumnSorting={disableColumnSorting}
    />
  );
};

export const SuitesTable = memo(SuitesTableComponent);
